import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiCharacterService, AICharacter } from '../../services/aiCharacterService';
import PageHeader from '../common/PageHeader';
import './AICharacterDetail.css';

interface AICharacterDetailProps {
  onToggleSidebar: () => void;
}

const AICharacterDetail: React.FC<AICharacterDetailProps> = ({ onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [character, setCharacter] = useState<AICharacter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (id) {
      fetchCharacter();
    }
  }, [id]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const menuPopups = document.querySelectorAll('.ai-character-detail-menu-popup');
      let clickedInsideMenu = false;

      menuPopups.forEach((popup) => {
        if (popup.contains(target)) {
          clickedInsideMenu = true;
        }
      });

      const menuButtons = document.querySelectorAll('.ai-character-detail-menu-btn');
      let clickedOnMenuButton = false;

      menuButtons.forEach((button) => {
        if (button.contains(target)) {
          clickedOnMenuButton = true;
        }
      });

      if (!clickedInsideMenu && !clickedOnMenuButton) {
        setOpenMenu(false);
        setMenuPosition(null);
      }
    };

    if (openMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenu]);

  // Update menu position on scroll/resize when menu is open
  useEffect(() => {
    if (!openMenu || !menuPosition) return;

    const updatePosition = () => {
      if (menuButtonRef.current) {
        const rect = menuButtonRef.current.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + window.scrollY + 4,
          right: window.innerWidth - rect.right + window.scrollX
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [openMenu, menuPosition]);

  const fetchCharacter = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await aiCharacterService.getCharacterById(id);
      if (response.success && response.character) {
        // Convert MySQL boolean (1/0) to JavaScript boolean
        const characterData = {
          ...response.character,
          is_active: Boolean(response.character.is_active)
        };
        setCharacter(characterData);
      } else {
        setError('Failed to fetch character details.');
      }
    } catch (err) {
      setError('An error occurred while fetching character details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const canGoBack = (): boolean => {
    return window.history.length > 1;
  };

  const handleBack = () => {
    if (canGoBack()) {
      navigate(-1);
    } else {
      navigate('/ai-characters');
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right + window.scrollX
      });
    }
    setOpenMenu(!openMenu);
  };

  const handleEdit = () => {
    setOpenMenu(false);
    setMenuPosition(null);
    if (id) {
      navigate(`/ai-characters/${id}/edit`);
    }
  };

  const handleDelete = async () => {
    setOpenMenu(false);
    setMenuPosition(null);
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this character?')) {
      return;
    }
    try {
      const response = await aiCharacterService.deleteCharacter(id);
      if (response.success) {
        navigate('/ai-characters');
      } else {
        alert('Failed to delete character.');
      }
    } catch (err) {
      alert('An error occurred while deleting the character.');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="ai-character-detail">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-title">/ai-characters</h1>}
        />
        <div className="ai-character-detail-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !character) {
    return (
      <div className="ai-character-detail">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-title">/ai-characters</h1>}
        />
        <div className="ai-character-detail-content">
          <p className="ai-character-detail-error">{error || 'Character not found.'}</p>
        </div>
      </div>
    );
  }

  const leftActions = (
    <>
      {canGoBack() && (
        <button className="ai-character-detail-back-btn" onClick={handleBack}>
          ← Back
        </button>
      )}
    </>
  );

  const rightActions = (
    <div className="ai-character-detail-actions">
      <button 
        ref={menuButtonRef}
        className="ai-character-detail-menu-btn" 
        onClick={handleMenuClick}
        title="Character options"
      >
        ⋯
      </button>
      {openMenu && menuPosition && createPortal(
        <div 
          className="ai-character-detail-menu-popup"
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            zIndex: 1000
          }}
        >
          <button 
            className="ai-character-detail-menu-option"
            onClick={handleEdit}
          >
            Edit
          </button>
          <div className="ai-character-detail-menu-separator"></div>
          <button 
            className="ai-character-detail-menu-option delete"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>,
        document.body
      )}
    </div>
  );

  return (
    <div className="ai-character-detail">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-title">{character.name}</h1>}
        leftActions={leftActions}
        rightActions={rightActions}
      />
      <div className="ai-character-detail-content">
        <div className="ai-character-detail-section">
          <h2 className="ai-character-detail-section-title">/details</h2>
          <div className="ai-character-detail-info">
            <div className="ai-character-detail-info-item">
              <span className="ai-character-detail-info-label">Model Provider:</span>
              <span className="ai-character-detail-info-value">{character.model_provider}</span>
            </div>
            <div className="ai-character-detail-info-item">
              <span className="ai-character-detail-info-label">Model Name:</span>
              <span className="ai-character-detail-info-value ai-character-detail-info-value-monospace">
                /{character.model_name}
              </span>
            </div>
            <div className="ai-character-detail-info-item">
              <span className="ai-character-detail-info-label">Status:</span>
              <span className={`ai-character-detail-status-badge ${character.is_active ? 'active' : 'inactive'}`}>
                {character.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="ai-character-detail-info-item">
              <span className="ai-character-detail-info-label">Created:</span>
              <span className="ai-character-detail-info-value">
                {new Date(character.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="ai-character-detail-info-item">
              <span className="ai-character-detail-info-label">Updated:</span>
              <span className="ai-character-detail-info-value">
                {new Date(character.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="ai-character-detail-section">
          <h2 className="ai-character-detail-section-title">/persona</h2>
          <div className="ai-character-detail-persona">
            <pre className="ai-character-detail-persona-json">
              {JSON.stringify(character.persona_info, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICharacterDetail;

